<!-- eslint-disable max-len -->
<template>
  <Layout v-if="state$$.isVisible">
    <template #body>
      <Card
        v-if="appState$$.currentRequest"
        :title="`Registro de solicitud`"
      >
        <template #body>
          <div class="pending-request">
            <div
              v-if="isLoading"
              class="pending-request__icon-container"
            >
              <ProgressSpinner
                stroke-width="5"
                class="app-in-loading__spinner pending-request__icon-container__spinner"
                fill="var(--primary-color)"
              />
            </div>
            <div class="pending-request__info">
              <span class="pending-request__info__register">
                {{ modelRef.pendingRegisterText }}
                {{ registerDate(appState$$?.currentRequest?.serverDate) }}
              </span>
              <div class="pending-request__info__pending">
                <span class="pending-request__info__pending__alert">
                  {{ modelRef.pendingAlertText }}
                </span>
                <span class="pending-request__info__pending__text">
                  <div
                    v-if="isLoading"
                    class="pending-request__info__pending__text__loading"
                  >
                    <p>Estamos verificando la información de su solicitud. </p>
                    <p>Este proceso puede tardar unos segundos.</p>
                    <p>No abandone la página.</p>
                  </div>
                  <div
                    v-else
                    class="pending-request__info__pending__text__fail"
                  >
                    <p>Hemos registrado tu solicitud correctamente y está pendiente de verificar.</p>
                    <p>Te enviaremos un mensaje cuando se haya completado la verificación.</p>
                    <p>También puedes volver en cualquier momento para consultar el estado de tu registro: <a :href="urlSite">{{ urlSite }}</a></p>
                    <p>Gracias.</p>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </template>
  </Layout>
</template>

<script setup>
// #region VUE
import {
  inject,
  onMounted,
  onUnmounted,
  reactive,
  shallowRef,
  ref,
} from 'vue';
// #endregion

// #region STTEDRA
import {
  useDev,
  useImmer,
  useNormalizer,
} from '@sttedra_ui/framework';
// #endregion

// #region COMPONENTS COMPOSABLES
import {useRouter} from 'vue-router';
import useHelpers from '@/composables/helpers';

import Layout from '@/components/layout/Layout.vue';
import Card from '@/components/card/Card.vue';

import ProgressSpinner from 'primevue/progressspinner/sfc';

import useTransformToTimestampString from '@/composables/transform-timestamp-string';
// #endregion

// #region MODELS AND STATE
import PendingRequestViewModel from './pending-request.view-model';
import PendingRequestViewState from './pending-request.view-state';

import RegisterUserViewModel from '@/shared/detail-resquet.view-model';
// #endregion

// #region API
import {StatusCodes as HttpStatus} from 'http-status-codes';
import {getRequestByPulling as getRequestByPullingApi} from '@/api/register/register.api';
// #endregion

// #region Params, Ref, Reactives, Computed
const appState$$ = inject('appState$$');

const state = new PendingRequestViewState();
const state$$ = reactive(state);
const model = new PendingRequestViewModel();
const {ref: modelRef} = useImmer(model);

const {transformTimestampStringNotDate} = useTransformToTimestampString();
const {getMadridUtcOffsetHours} = useHelpers();

const router = useRouter();

let timePulling = Number(STTEDRA_PULLING_INIT_INTERVAL) || 500;
let step = 0; // Step STTEDRA_PULLING_MAX_CYCLE Finish

const MAX_CYCLE = Number(STTEDRA_PULLING_MAX_CYCLE) || 6;
const INCREMENT_INTERVAL = Number(STTEDRA_PULLING_INCREMENT_INTERVAL) || 1.3;
const isLoading = shallowRef(true);
const typePortal = ref(STTEDRA_TYPE_PORTAL);

const urlSite = ref(typePortal.value === 'verano-joven' ?
  'https://veranojoven.transportes.gob.es' : 'https://codigoviajero.transportes.gob.es/');

const {
  getObjectFilled,
  getBoolean,
} = useNormalizer();
// #endregion

// #region Functions
function microPulling() {
  setTimeout(async () => {
    const {
      response,
      data,
      error,
    } = await getRequestByPullingApi({
      code: appState$$.currentRequest?.code,
      identificationNumber: appState$$.currentRequest?.identificationNumber,
    }, appState$$.currentRequest?.temporalToken);

    if (response?.status !== HttpStatus.OK) {
      // ERROR CANCEL
      useDev.error('[PendingRequest.vue] microPulling', error);
      isLoading.value = false;
      return;
    }

    if (getObjectFilled(data) && getBoolean(data.notificable)) {
      // OK
      const formData = new RegisterUserViewModel(appState$$.currentRequest);
      appState$$.detailRequest = {
        ...formData || {},
        ...data,
        temporalToken: appState$$.currentRequest?.temporalToken,
      };
      isLoading.value = false;
      router.push({name: `DetailRequest`});
      return;
    }

    if (step >= MAX_CYCLE) {
      // TIMEOUT
      appState$$.detailRequest = null;
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    step = step + 1;
    timePulling = timePulling * INCREMENT_INTERVAL;
    microPulling();
  }, timePulling);
}

function registerDate(dateString) {
  const date = transformTimestampStringNotDate(dateString);
  const offset = getMadridUtcOffsetHours(dateString);

  if (offset === 1) {
    return `${date} (CET - GMT+1)`;
  }
  if (offset === 2) {
    return `${date} (CEST - GMT+2)`;
  }
  return `${date}`;
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  state$$.isVisible = false;

  if (appState$$.isOffline || !appState$$.currentRequest) {
    router.push({name: `Home`});
    return;
  }

  if (appState$$.currentRequest?.validationType === 'Online') {
    microPulling();
  } else {
    isLoading.value = false;
  }

  state$$.isVisible = true;
});

onUnmounted(() => {
  appState$$.currentRequest = null;
});
// #endregion
</script>

<style lang="scss" scoped>
@use '@/styles/vars' as *;
@use '@/styles/breakpoints' as *;

.pending-request{
  -webkit-box-align: center;
      -ms-flex-align: center;
          align-items: center;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
      -ms-flex-direction: column;
          flex-direction: column;
  gap: $brand__boxspace--medium * 2;
  -webkit-box-pack: center;
      -ms-flex-pack: center;
          justify-content: center;

  &__icon-container{
    border-radius: $pv__br--circle;
    color: $pv__color--white;
    width: -webkit-fit-content;
    width: -moz-fit-content;
    width: fit-content;
    &__spinner{
      width: 100px !important;
      height: 100px !important;
    }
  }

  &__info{
    -webkit-box-align: center;
        -ms-flex-align: center;
            align-items: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
        -ms-flex-direction: column;
            flex-direction: column;
    -webkit-box-flex: 1;
        -ms-flex: 1;
            flex: 1;
    gap:($brand__boxspace--medium * 2);
    -webkit-box-pack: center;
        -ms-flex-pack: center;
            justify-content: center;

    @include media("<=phone") {
      font-size: $pv__fs--xlarge--phone;
      text-align: center;
    }
    @include media(">phone") {
      font-size: $pv__fs--xlarge;
    }

    &__register{
      font-weight: 600;
    }

    &__pending{
      -webkit-box-align: center;
          -ms-flex-align: center;
              align-items: center;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
          -ms-flex-direction: column;
              flex-direction: column;
      gap:($brand__boxspace--medium * 2);
      -webkit-box-pack: center;
          -ms-flex-pack: center;
              justify-content: center;
        &__text{
          &__loading, &__fail{
            p {
              text-align: center;
              margin: 0.8rem 0 1rem 0;
              font-size: 1.2rem;
            }
          }
        }
      &__alert{
        color: var(--primary-600);
        font-weight: 800;
        font-size: 1.8rem;
      }
    }
  }
}
</style>
